import Images from "@/components/Images";
import { updatePost } from "@/graphql/mutations";
import { uploadFile } from "@/hooks/useUploadFile";
import { imageToBase64 } from "@/utils/helper";
import { DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, notification, Tooltip, Upload } from "antd";
import { RcFile } from "antd/es/upload";
import { API } from "aws-amplify";
import React, { useEffect, useState } from "react";
import { useAnalytics } from "@/hooks/useAnalytics";

const ModalPostEdit = ({
  open,
  data,
  onCancel,
  onSuccessEdit,
}: {
  open: boolean;
  data: { content: string; image: any; id: string; _version: number };
  onCancel: any;
  onSuccessEdit: any;
}) => {
  const [form] = Form.useForm();
  const watcher = Form.useWatch([], form);
  const [isBase64, isBase64Set] = useState(false);
  const [originalFile, originalFileSet] = useState<RcFile | undefined>();
  const [isLoading, isLoadingSet] = useState(false);
  const { track } = useAnalytics()

  useEffect(() => {
    form.setFieldValue("content", data?.content);
    form.setFieldValue("image", data?.image);
  }, [open]);

  async function onEditPost() {
    try {
      isLoadingSet(true);
      const keyImg = `users/post/${Date.now()}-${originalFile?.name}`;
      if (isBase64) {
        await uploadFile({
          key: keyImg,
          file: originalFile,
        });
      }
      await API.graphql({
        query: updatePost,
        variables: {
          input: {
            id: data?.id,
            content: watcher?.content,
            attachments: [isBase64 ? keyImg : watcher?.imag ? watcher?.image : data?.image],
            _version: data?._version,
          },
        },
      });
      isLoadingSet(false);
      onSuccessEdit();
      isBase64Set(false)
      form.resetFields();
      track("Form Edit Post", "Submit", "Success")
    } catch (error: any) {
      track("Form Edit Post", "Submit", "Failed")
      isLoadingSet(false);
      notification.error({ message: error?.errors?.[0]?.message });
    }
  }

  return (
    <Modal
      open={open}
      title="Edit Post"
      footer={
        <Button onClick={onEditPost} loading={isLoading} type="primary" disabled={!watcher?.content}>
          Edit
        </Button>
      }
      onCancel={() => {
        if (!isLoading) {
          onCancel();
          form.resetFields();
          isBase64Set(false);
        }
      }}
      confirmLoading={isLoading}
    >
      <Form form={form} layout="vertical">
        <Form.Item label="Caption" required name="content" >
          <Input disabled={isLoading} />
        </Form.Item>
        <Form.Item name="image" >
          <div className="flex justify-between w-full">
            <span>Post File</span>
            {watcher?.image && <div
              role="button"
              className="hover:text-red-500"
              onClick={() => form.setFieldValue("image", null)}
            >
              <DeleteOutlined className="text-base" />
            </div>}
          </div>

          {watcher?.image ? (
            <div className={"border border-neutral-200 p-2 flex justify-between items-center rounded-md" + (isLoading ? " opacity-50 hover:opacity-75 cursor-not-allowed bg-[#292929]" : "")}>
              {(() => {
                const filePath = form.getFieldValue("image");
                const regexSupportedFile = /\.(jpg|jpeg|png|gif|bmp|webp|svg|xls|xlsx|pdf)$/i
                const isSupportedFile = filePath?.match(regexSupportedFile) ||
                  (originalFile?.name && originalFile.name.match(regexSupportedFile));

                if (isSupportedFile) {
                  return (
                    <Images
                      originalFile={originalFile}
                      src={filePath}
                      staticImg={isBase64}
                      alt={data?.content}
                      width={300}
                      height={200}
                      className="object-cover rounded-md"
                    />
                  );
                } else {
                  const fileName = originalFile?.name
                    ? originalFile.name
                    : filePath?.split('/')?.pop()?.split('-')?.slice(1)?.join('-');
                  return (
                    <Tooltip title={fileName}>
                      <p className="w-2/3 truncate mb-0">{fileName}</p>
                    </Tooltip>
                  );
                }
              })()}
            </div>
          ) : (
            <div className="border border-[#8C8C8C] p-2 h-[50px] flex justify-between items-center rounded-md">
              <p>No File</p>
              <Upload
                accept="*"
                fileList={[]}
                onChange={async (e) => {
                  originalFileSet(e.file.originFileObj);
                  const rawImg = await imageToBase64(
                    e.file.originFileObj as RcFile,
                  );
                  isBase64Set(true);
                  form.setFieldValue("image", rawImg);
                }}
              >
                <Button>Attach File</Button>
              </Upload>
            </div>
          )}
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default React.memo(ModalPostEdit);
